// @ts-strict-ignore
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useConfirmPayee } from 'hooks/queries/useConfirmPayee'
import { BeneficiaryDetailsCurrency, BeneficiaryTransferDestination } from 'types/beneficiaries'
import { ConfirmPayeeBeneficiaryParams, ConfirmPayeeMatchStatuses } from 'types/payoutsV2'

interface UseConfirmBeneficiaryCalloutProps {
  beneficiaryDetails: ConfirmPayeeBeneficiaryParams
  isPayout?: boolean
  isContact?: boolean
}

export const useConfirmBeneficiaryCallout = ({
  beneficiaryDetails,
  isPayout = true,
  isContact = false,
}: UseConfirmBeneficiaryCalloutProps) => {
  const { t } = useTranslation()

  const request = useMemo(() => {
    const req = {
      ...beneficiaryDetails,
    }

    delete req?.transferDestination

    return req
  }, [beneficiaryDetails])

  const isVopCheckEnabled = useMemo(() => {

    if (request?.currency !== BeneficiaryDetailsCurrency.EUR) {
      return false
    }

    if (isContact) {
      return true
    }

    return (
      !!request && beneficiaryDetails?.transferDestination === BeneficiaryTransferDestination.LOCAL
    )
  }, [request, beneficiaryDetails, isContact])

  const {
    data: confirmPayeeData,
    error: confirmPayeeDataError,
    isFetching: isConfirmingPayee,
  } = useConfirmPayee({
    request,
    enabled: isVopCheckEnabled,
  })

  const accountNumberFormat = useMemo(() => {
    switch (beneficiaryDetails?.currency) {
      case BeneficiaryDetailsCurrency.EUR:
      case BeneficiaryDetailsCurrency.AED:
        return t('manageBeneficiaries.iban')
      case BeneficiaryDetailsCurrency.GBP:
      case BeneficiaryDetailsCurrency.ZAR:
        return t('manageBeneficiaries.accountNumber')
      case BeneficiaryDetailsCurrency.USD:
        return beneficiaryDetails?.transferDestination ===
          BeneficiaryTransferDestination.INTERNATIONAL
          ? t('manageBeneficiaries.accountNumberOrIban')
          : t('manageBeneficiaries.accountNumber')
      default:
        return t('manageBeneficiaries.accountNumberOrIban')
    }
  }, [beneficiaryDetails])

  const confirmationOfPayeeCallout = useMemo(() => {
    const isSwift =
      beneficiaryDetails?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL

    if (isSwift || (!confirmPayeeData && !confirmPayeeDataError)) {
      return
    }

    if (confirmPayeeDataError) {
      return isPayout
        ? t('wallets.fiatSend.verificationOfPayee.verificationNotPossibleTechnicalError', {
            accountNumberFormat,
          })
        : t('manageBeneficiaries.confirmationOfPayee.verificationNotPossibleTechnicalError', {
            accountNumberFormat,
          })
    }

    const translationBaseKey = isPayout
      ? 'wallets.fiatSend.verificationOfPayee'
      : 'manageBeneficiaries.confirmationOfPayee'

    switch (confirmPayeeData?.matchStatus) {
      case ConfirmPayeeMatchStatuses.MATCH:
        return t(`${translationBaseKey}.match`, { accountNumberFormat })
      case ConfirmPayeeMatchStatuses.PARTIAL_MATCH:
        return t(`${translationBaseKey}.closeMatch`, {
          actualName: confirmPayeeData?.actualName,
          accountNumberFormat,
        })
      case ConfirmPayeeMatchStatuses.NO_MATCH:
        return t(`${translationBaseKey}.noMatch`, { accountNumberFormat })
      case ConfirmPayeeMatchStatuses.TECHNICAL_ERROR:
        return t(`${translationBaseKey}.verificationNotPossibleTechnicalError`, {
          accountNumberFormat,
        })
      case ConfirmPayeeMatchStatuses.NOT_POSSIBLE:
        return t(`${translationBaseKey}.verificationNotPossible`, {
          accountNumberFormat,
        })
      case ConfirmPayeeMatchStatuses.NOT_REQUIRED:
      default:
        return
    }
  }, [confirmPayeeData, confirmPayeeDataError, accountNumberFormat, isPayout])

  const editBeneficiary = useMemo(() => {
    const isNonEditableStatus =
      confirmPayeeData?.matchStatus === ConfirmPayeeMatchStatuses.MATCH ||
      confirmPayeeData?.matchStatus === ConfirmPayeeMatchStatuses.NOT_POSSIBLE

    if (isContact) {
      return !isNonEditableStatus
    }

    return isPayout && confirmPayeeData && !isNonEditableStatus
  }, [isPayout, confirmPayeeData, isContact])

  return useMemo(() => {
    return {
      confirmPayeeData,
      isConfirmingPayee,
      confirmationOfPayeeCallout,
      editBeneficiary: editBeneficiary,
    }
  }, [confirmationOfPayeeCallout, isConfirmingPayee, confirmPayeeData, editBeneficiary])
}
